import './index.scss';

import { Component, ReactNode } from 'react';

import t from 'utils/i18n';
import StepperContext, { StepperProvider } from './context';
import { BraxAlertBar } from 'components/BraxAlertBar';
import { BraxButton } from 'components/BraxButton';
import { BraxNavbar } from 'components/BraxNavbar';

export type BraxStepperProps = {
    active?: string,
    previous?: ReactNode,
    next?: ReactNode,
    last?: ReactNode,
    indicator?: boolean,
    children: ReactNode,
    onStepChange?: (active: string) => void
    onEnd?: () => void
};

type BraxStepperState = {
    active?: string,
    lastActive?: string
};

export class BraxStepper extends Component<BraxStepperProps, BraxStepperState> {

    state: BraxStepperState = {
        active: this.props.active,
        lastActive: this.props.active
    };

    componentDidUpdate(prevProps: BraxStepperProps) {
        if (prevProps.active !== this.props.active)
            this.setState({ active: this.props.active });
    }

    goTo(active: string) {
        if (this.state.active !== active) {
            this.setState({ active });

            this.props.onStepChange?.(active);
        }
    }

    render() {
        return (
            <StepperProvider active={this.state.active}>
                <StepperContext.Consumer>
                    { context => {
                        const activeIndex = context.steps.findIndex(step => step.id === context.active);

                        let lastActiveIndex = context.steps.findIndex(step => step.id === this.state.lastActive);

                        if (lastActiveIndex < activeIndex)
                            setTimeout(() => this.setState({ lastActive: context.steps[activeIndex].id }));

                        if (lastActiveIndex === -1)
                            lastActiveIndex = activeIndex;

                        const isLastStep = activeIndex === context.steps.length - 1;

                        const nextStepIsEnable = isLastStep || context.steps[activeIndex + 1].enable;

                        const buttons = {
                            left: activeIndex !== 0 ? (
                                <BraxButton
                                    variant="secondary"
                                    size="md"
                                    onClick={() => this.goTo(context.steps[activeIndex - 1].id)}
                                >
                                    { this.props.previous || t('Voltar') }
                                </BraxButton>
                            ) : null,
                            right: !isLastStep ? (
                                <BraxButton
                                    key="next"
                                    variant="primary"
                                    size="md"
                                    disabled={!nextStepIsEnable}
                                    onClick={() => this.goTo(context.steps[activeIndex + 1].id)}
                                >
                                    { this.props.next || t('Próximo') }
                                </BraxButton>
                            ) : (
                                <BraxButton
                                    key="submit"
                                    type="submit"
                                    variant="primary"
                                    size="md"
                                    onClick={() => this.props.onEnd?.()}
                                >
                                    { this.props.last || t('Finalizar') }
                                </BraxButton>
                            )
                        };

                        return (
                            <div
                                className={`brax-stepper ${!nextStepIsEnable ? 'brax-next-step-disabled' : ''} ${this.props.indicator ? 'brax-with-indicator' : ''}`}
                                brax-step={context.active}
                            >
                                <BraxNavbar
                                    active={context.active}
                                    itens={context.steps.map((step, index) => ({
                                        key: step.id,
                                        content: step.title,
                                        disabled: index > lastActiveIndex || !step.enable
                                    }))}
                                    onChange={active => this.goTo(active as string)}
                                />

                                <div>
                                    <div style={{ '--current-index': activeIndex } as any}>{ this.props.children }</div>
                                </div>

                                { this.props.indicator ? (
                                    <BraxAlertBar open>
                                        <div>{ buttons.left || <div /> }</div>

                                        <div className="brax-stepper-indicator">
                                            <p>Etapa { activeIndex + 1 } de { context.steps.length }</p>

                                            <div>
                                                <div style={{ width: `${100 / context.steps.length * (activeIndex + 1) }%` }} />
                                            </div>
                                        </div>

                                        <div>{ buttons.right }</div>
                                    </BraxAlertBar>
                                ) : (
                                    <BraxAlertBar open>
                                        <div />

                                        <div>{ buttons.left } { buttons.right }</div>
                                    </BraxAlertBar>
                                ) }
                            </div>
                        );
                    } }
                </StepperContext.Consumer>
            </StepperProvider>
        );
    }

}
